













import { Component } from 'vue-property-decorator'
import { Chart } from 'highcharts-vue'
import { BaseQuestion } from '@/components/Survey/QuestionTypes/BaseQuestion'
import Tab from '@/components/Tabs/Tab.vue'
import Tabs from '@/components/Tabs/Tabs.vue'
import {
  calcWeightedAverage,
  generateRankIndexSeriesData,
  generateSlicedRankSeries,
  getCategoriesForSlicedRankQuestion,
  getCategoriesForSlicedVariantQuestion,
  getRankingTotals,
  NestedResponses,
  Series
} from '@/components/Survey/Utilities/ChartUtilities'
import { getOptionsTextFromQuestion } from '@/components/Survey/Utilities/SurveyUtilities'
import { generateSequentialColors } from '@/components/Survey/Utilities/ColorUtility'
import {
  indexRankChartToolTipFormatter,
  slicedChartTooltipFormatter
} from '@/components/Survey/Utilities/ChartTooltipUtilities'
import SliceNotSupported from '@/components/Survey/SliceNotSupported.vue'

@Component({
  components: {
    Highcharts: Chart,
    Tabs,
    Tab,
    SliceNotSupported
  }
})
export default class RankQuestion extends BaseQuestion {
  get weightedRankings () {
    const rankings = getRankingTotals(this.categories, this.indexResponses.map(response => response.answer))
    return calcWeightedAverage(rankings)
  }

  get indexSeriesData () {
    return generateRankIndexSeriesData(this.weightedRankings, this.colorConfiguration.sequentialPalette)
  }

  get indexSeries () {
    return [
      {
        dataLabels: {
          enabled: true,
          crop: false,
          overflow: 'allow',
          allowOverlap: true,
          inside: false
        },
        visible: true,
        data: this.indexSeriesData,
        showInLegend: false
      }
    ]
  }

  getSlicedSeries (responses: NestedResponses) {
    const count = Object.keys(this.weightedRankings).length
    const colors = generateSequentialColors(count, this.colorConfiguration.sequentialPalette)
    const slicedSeriesData = generateSlicedRankSeries(responses, colors, this.sortedCategories)

    const sortedData: Series[] = []
    this.sortedCategories.forEach(category => {
      const series = slicedSeriesData.find(o => o.name === category)
      if (series) {
        sortedData.push(series)
      }
    })

    return sortedData
  }

  get responses () {
    if (this.isSliced && this.isVariant) {
      return this.getSlicedSeries(this.typedSlicedVariantResponses[this.selectedTab])
    } else if (this.isSliced || this.isVariant) {
      return this.getSlicedSeries(this.typedSlicedResponses)
    } else {
      return this.indexSeries
    }
  }

  generateChartOptions (series: Series[]) {
    return {
      ...this.defaultChartOptions(this.displayCategories),
      caption: {
        useHTML: true,
        text: this.captionText
      },
      yAxis: {
        ...this.defaultChartOptions(this.displayCategories).yAxis,
        labels: {
          ...this.defaultChartOptions(this.displayCategories).yAxis.labels,
          format: '{value}'
        }
      },
      exporting: {
        ...this.defaultChartOptions(this.displayCategories).exporting,
        chartOptions: {
          ...this.defaultChartOptions(this.displayCategories).exporting.chartOptions,
          caption: {
            text: this.exportCaptionText
          }
        }
      },
      xAxis: {
        ...this.defaultChartOptions(this.displayCategories).xAxis,
        categories: this.displayCategories
      },
      series,
      tooltip: this.isSliced || this.isVariant
        ? slicedChartTooltipFormatter(this.segmentTotals, this.question.type + '-weighted-average', this.sliceByQuestion?.type)
        : indexRankChartToolTipFormatter(this.indexTotals)
    }
  }

  get sortedCategories () {
    const categories: string[] = []
    const seriesData = this.indexSeriesData
    seriesData.forEach(series => {
      categories.push(series.name!)
    })
    return categories
  }

  get displayCategories () {
    if (this.isSliced && this.isVariant) {
      return getCategoriesForSlicedVariantQuestion(this.typedSlicedVariantResponses, this.selectedTab)
    } else if (this.isSliced || this.isVariant) {
      return getCategoriesForSlicedRankQuestion(this.typedSlicedResponses, this.sortedCategories, this.sliceByQuestion)
    } else {
      return this.sortedCategories
    }
  }

  get categories () {
    return getOptionsTextFromQuestion(this.question).map(category => category.text)
  }

  get captionText () {
    return 'To determine which item was ranked highest, Feedback Loop generates a score to represent the average ranking of each item.' +
           ' Learn more about how we <a href="https://help.alphahq.com/en/articles/4271509-how-we-calculate-weighted-average-score" ' +
           'target="_blank">calculate weighted average</a> score.'
  }

  get exportCaptionText () {
    return 'Feedback Loop generates a score to represent the average ranking for each item. Visit our Help Center to learn more about calculating weighted average.'
  }
}
